import * as React from "react";
import { ChevronDown, Check } from "lucide-react";
import { cn } from "@shared/lib/utils";

/**
 * Simple Select component for dropdown selections
 * Built without external dependencies for consistency
 */

// Helper to extract text content from React children
function extractTextContent(node) {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractTextContent).filter(Boolean).join('');
  if (node?.props?.children) return extractTextContent(node.props.children);
  return '';
}

const SelectContext = React.createContext(null);

const Select = React.forwardRef(({ children, value, onValueChange, defaultValue, ...props }, ref) => {
  const [internalValue, setInternalValue] = React.useState(defaultValue || '');
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectedLabel, setSelectedLabel] = React.useState('');
  const wrapperRef = React.useRef(null);
  
  const currentValue = value !== undefined ? value : internalValue;
  
  const handleValueChange = React.useCallback((newValue, label) => {
    if (value === undefined) {
      setInternalValue(newValue);
    }
    setSelectedLabel(label || '');
    onValueChange?.(newValue);
    setIsOpen(false);
  }, [value, onValueChange]);
  
  // Merge external ref with internal wrapperRef
  const mergedRef = React.useCallback((node) => {
    wrapperRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  }, [ref]);

  return (
    <SelectContext.Provider value={{ 
      value: currentValue, 
      onValueChange: handleValueChange, 
      isOpen, 
      setIsOpen,
      selectedLabel,
      setSelectedLabel,
      wrapperRef,
    }}>
      <div ref={mergedRef} className="relative inline-block w-full" {...props}>
        {children}
      </div>
    </SelectContext.Provider>
  );
});
Select.displayName = "Select";

const SelectTrigger = React.forwardRef(({ className, children, ...props }, ref) => {
  const context = React.useContext(SelectContext);
  
  return (
    <button
      ref={ref}
      type="button"
      onClick={() => context?.setIsOpen(!context?.isOpen)}
      className={cn(
        "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        className
      )}
      {...props}
    >
      {children}
      <ChevronDown className="h-4 w-4 opacity-50" />
    </button>
  );
});
SelectTrigger.displayName = "SelectTrigger";

const SelectValue = React.forwardRef(({ className, placeholder, children, ...props }, ref) => {
  const context = React.useContext(SelectContext);
  const displayText = children || context?.selectedLabel || placeholder;
  return (
    <span ref={ref} className={cn("block truncate", !children && !context?.selectedLabel && "text-muted-foreground", className)} {...props}>
      {displayText}
    </span>
  );
});
SelectValue.displayName = "SelectValue";

const SelectContent = React.forwardRef(({ className, children, side: _side, ...props }, _ref) => {
  const context = React.useContext(SelectContext);
  const contentRef = React.useRef(null);
  const [style, setStyle] = React.useState(null);

  // Calculate position for dropdown, compensating for CSS transforms
  // (e.g. Radix Dialog centering) which create a new containing block
  React.useLayoutEffect(() => {
    if (!context?.isOpen || !context?.wrapperRef?.current) return;
    const wrapper = context.wrapperRef.current;
    const rect = wrapper.getBoundingClientRect();

    // Find any ancestor with a CSS transform — this changes the
    // coordinate system for position:fixed children
    let transformOffset = { x: 0, y: 0 };
    let el = wrapper.parentElement;
    while (el && el !== document.documentElement) {
      const cs = window.getComputedStyle(el);
      if (cs.transform && cs.transform !== 'none') {
        const elRect = el.getBoundingClientRect();
        transformOffset = { x: elRect.left, y: elRect.top };
        break;
      }
      el = el.parentElement;
    }

    const adjustedLeft = rect.left - transformOffset.x;
    const adjustedTop  = rect.bottom - transformOffset.y + 4;
    const adjustedBottom = (window.innerHeight - rect.top) - transformOffset.y + 4; // Not used for bottom calc in transform context

    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const dropdownHeight = Math.min(240, 40 * 7);
    const openUpward = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

    setStyle({
      position: 'fixed',
      left: adjustedLeft,
      width: rect.width,
      ...(openUpward
        ? { bottom: (viewportHeight - rect.top - transformOffset.y + 4), top: 'auto' }
        : { top: adjustedTop, bottom: 'auto' }),
    });
  }, [context?.isOpen]);

  // Close on click outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        contentRef.current && !contentRef.current.contains(event.target) &&
        context?.wrapperRef?.current && !context.wrapperRef.current.contains(event.target)
      ) {
        context?.setIsOpen(false);
      }
    };
    if (context?.isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [context?.isOpen]);

  // Always render children (hidden when closed) so SelectItems can sync labels
  if (!context?.isOpen || !style) {
    return <div style={{ display: 'none', position: 'absolute', pointerEvents: 'none' }}>{children}</div>;
  }

  return (
    <div
      ref={contentRef}
      className={cn(
        "z-[9999] max-h-60 overflow-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95",
        className
      )}
      style={style}
      {...props}
    >
      {children}
    </div>
  );
});
SelectContent.displayName = "SelectContent";

const SelectItem = React.forwardRef(({ className, children, value, textValue, ...props }, ref) => {
  const context = React.useContext(SelectContext);
  const isSelected = context?.value === value;
  const label = textValue || extractTextContent(children);

  // Sync label into context when this item is the selected one (on mount / value change)
  React.useEffect(() => {
    if (isSelected && label) {
      context?.setSelectedLabel(label);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelected, label]);
  
  return (
    <div
      ref={ref}
      onClick={() => context?.onValueChange(value, label)}
      className={cn(
        "relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
        isSelected && "bg-accent text-accent-foreground",
        className
      )}
      {...props}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        {isSelected && <Check className="h-4 w-4" />}
      </span>
      {children}
    </div>
  );
});
SelectItem.displayName = "SelectItem";

const SelectGroup = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-1", className)} {...props} />
));
SelectGroup.displayName = "SelectGroup";

const SelectLabel = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("py-1.5 pl-8 pr-2 text-sm font-semibold", className)}
    {...props}
  />
));
SelectLabel.displayName = "SelectLabel";

const SelectSeparator = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-muted", className)}
    {...props}
  />
));
SelectSeparator.displayName = "SelectSeparator";

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
